import React, { useState, useEffect, useRef } from 'react';
import { Edit2, Save, X, ChevronDown, Trash2 } from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dropdown options
  const [blockOptions, setBlockOptions] = useState([]);
  const [panchayatOptions, setPanchayatOptions] = useState([]);
  
  // Dropdown states
  const [blockSearch, setBlockSearch] = useState('');
  const [panchayatSearch, setPanchayatSearch] = useState('');
  const [showBlockDropdown, setShowBlockDropdown] = useState(false);
  const [showPanchayatDropdown, setShowPanchayatDropdown] = useState(false);
  
  // State for expanded panchayat rows
  const [expandedPanchayats, setExpandedPanchayats] = useState({});
  
  const blockDropdownRef = useRef(null);
  const panchayatDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    role: '',
    contact: '',
    blocks: [],
    panchayat: []
  });

  const roleOptions = [
    { value: 'state', label: 'State Admin' },
    { value: 'dist-ac', label: 'Additional Collector' },
    { value: 'dist-ddo', label: 'District Development Officer' },
    { value: 'dist-bdo', label: 'Block Development Officer' },
    { value: 'pncyt-pre', label: 'Panchayat President' },
    { value: 'pncyt-bdo', label: 'Zonal Deputy BDO' },
    { value: 'pncyt-ae', label: 'Assistant Engineer' },
    { value: 'pncyt-je', label: 'Junior Engineer' },
    { value: 'overseer', label: 'Overseer' },
    { value: 'motivator', label: 'Motivator' },
    { value: 'zonal', label: 'Zonal' }
  ];

  useEffect(() => {
    fetchUsers();
    fetchBlocks();
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (blockDropdownRef.current && !blockDropdownRef.current.contains(event.target)) {
        setShowBlockDropdown(false);
      }
      if (panchayatDropdownRef.current && !panchayatDropdownRef.current.contains(event.target)) {
        setShowPanchayatDropdown(false);
      }
    };

    // Close dropdowns with Escape key
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowBlockDropdown(false);
        setShowPanchayatDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        setUsers(data.data || []);
      } else {
        setError(data.message || 'Failed to fetch users');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBlocks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/blocks`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.data) {
        setBlockOptions(data.data);
      } else {
        setBlockOptions([]);
      }
    } catch (err) {
      console.error('Error fetching blocks:', err);
      setBlockOptions([]);
    }
  };

  const fetchPanchayats = async (blockName) => {
    if (!blockName) {
      setPanchayatOptions([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/${blockName}/panchayats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.data) {
        console.log('Fetched panchayats for', blockName, ':', data.data);
        setPanchayatOptions(data.data);
      } else {
        console.log('No panchayats found for', blockName);
        setPanchayatOptions([]);
      }
    } catch (err) {
      console.error('Error fetching panchayats:', err);
      setPanchayatOptions([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear blocks and panchayats when role changes
    if (name === 'role' && !['dist-bdo', 'zonal'].includes(value)) {
      setFormData(prev => ({ ...prev, blocks: [] }));
      setBlockSearch('');
    }
    if (name === 'role' && !['dist-bdo', 'pncyt-pre', 'pncyt-bdo', 'pncyt-ae', 'pncyt-je', 'overseer', 'motivator', 'zonal'].includes(value)) {
      setFormData(prev => ({ ...prev, panchayat: [] }));
      setPanchayatSearch('');
    }
  };

  const filteredBlocks = blockOptions.filter(block =>
    (block.name || block).toLowerCase().includes(blockSearch.toLowerCase())
  );

  const filteredPanchayats = panchayatOptions.filter(panchayat =>
    (panchayat.name || panchayat).toLowerCase().includes(panchayatSearch.toLowerCase())
  );

  const handleBlockToggle = (block) => {
    const blockName = block.name || block;
    const currentBlocks = [...formData.blocks];
    
    if (currentBlocks.includes(blockName)) {
      // Remove block
      setFormData(prev => ({
        ...prev,
        blocks: currentBlocks.filter(b => b !== blockName),
        panchayat: [] // Clear panchayats when blocks change
      }));
    } else {
      // Add block
      const newBlocks = [...currentBlocks, blockName];
      setFormData(prev => ({
        ...prev,
        blocks: newBlocks,
        panchayat: [] // Clear panchayats when blocks change
      }));
      // Fetch panchayats for the first selected block
      if (newBlocks.length === 1) {
        fetchPanchayats(blockName);
      }
    }
    // Don't close dropdown - let user continue selecting
  };

  const handleSelectAllBlocks = () => {
    const allBlockNames = blockOptions.map(block => block.name || block);
    const isAllSelected = allBlockNames.every(blockName => formData.blocks.includes(blockName));
    
    if (isAllSelected) {
      // Deselect all
      setFormData(prev => ({
        ...prev,
        blocks: [],
        panchayat: []
      }));
      setPanchayatOptions([]);
    } else {
      // Select all
      setFormData(prev => ({
        ...prev,
        blocks: allBlockNames,
        panchayat: []
      }));
      
      if (allBlockNames.length > 0) {
        fetchPanchayats(allBlockNames[0]);
      }
    }
  };

  const handlePanchayatToggle = (panchayat) => {
    const panchayatName = panchayat.name || panchayat;
    const currentPanchayats = [...formData.panchayat];
    
    if (currentPanchayats.includes(panchayatName)) {
      // Remove panchayat
      setFormData(prev => ({
        ...prev,
        panchayat: currentPanchayats.filter(p => p !== panchayatName)
      }));
    } else {
      // Add panchayat
      setFormData(prev => ({
        ...prev,
        panchayat: [...currentPanchayats, panchayatName]
      }));
    }
    // Don't close dropdown - let user continue selecting
  };

  const handleSelectAllPanchayats = () => {
    const allPanchayatNames = panchayatOptions.map(panchayat => panchayat.name || panchayat);
    const isAllSelected = allPanchayatNames.every(panchayatName => formData.panchayat.includes(panchayatName));
    
    if (isAllSelected) {
      // Deselect all
      setFormData(prev => ({
        ...prev,
        panchayat: []
      }));
    } else {
      // Select all
      setFormData(prev => ({
        ...prev,
        panchayat: allPanchayatNames
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      contact: '',
      blocks: [],
      panchayat: []
    });
    setBlockSearch('');
    setPanchayatSearch('');
    setError('');
    setSuccess('');
    setEditingUser(null);
    setPanchayatOptions([]);
  };

  const handleEdit = async (user) => {
    console.log('Editing user:', user);
    
    setEditingUser(user);
    
    // Normalize panchayat data
    let normalizedPanchayat = [];
    if (user.panchayat) {
      if (Array.isArray(user.panchayat)) {
        normalizedPanchayat = user.panchayat;
      } else if (typeof user.panchayat === 'string') {
        normalizedPanchayat = [user.panchayat];
      }
    }
    
    // Normalize blocks data
    let normalizedBlocks = [];
    if (user.blocks) {
      if (Array.isArray(user.blocks)) {
        normalizedBlocks = user.blocks;
      } else if (typeof user.blocks === 'string') {
        normalizedBlocks = [user.blocks];
      }
    }
    
    setFormData({
      name: user.name || '',
      role: user.role || '',
      contact: user.username || user.contact || '',
      blocks: normalizedBlocks,
      panchayat: normalizedPanchayat
    });
    
    // Fetch panchayats for selected blocks when editing
    if (normalizedBlocks.length > 0) {
      setBlockSearch('');
      await fetchPanchayats(normalizedBlocks[0]);
    }
    
    setShowForm(true);
    
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

const handleSubmit = async () => {
    if (!formData.name || !formData.role) {
      setError('Please fill in all required fields');
      return;
    }

    setCreating(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: [formData.name],
        role: [formData.role],
        username: [formData.contact]
      };

      // Add blocks field for roles that need it - wrap each block in its own array
// Add blocks field for roles that need it
      if (['dist-bdo', 'pncyt-pre', 'pncyt-bdo', 'pncyt-ae', 'pncyt-je', 'overseer', 'motivator', 'zonal'].includes(formData.role) && formData.blocks.length > 0) {
        payload.blocks = Array.isArray(formData.blocks) ? formData.blocks : [formData.blocks];
      }

      // Add panchayat field for all roles that need it
      if (['dist-bdo', 'pncyt-pre', 'pncyt-bdo', 'pncyt-ae', 'pncyt-je', 'overseer', 'motivator', 'zonal'].includes(formData.role)) {
        payload.panchayat = Array.isArray(formData.panchayat) ? formData.panchayat : (formData.panchayat ? [formData.panchayat] : []);
      }

      const url = editingUser 
        ? `${API_BASE_URL}/user/${editingUser.id}`
        : `${API_BASE_URL}/user`;
      
      const method = editingUser ? 'PUT' : 'POST';

      console.log('Sending payload:', JSON.stringify(payload, null, 2));

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(editingUser ? 'User updated successfully!' : 'User created successfully!');
        resetForm();
        setShowForm(false);
        fetchUsers();
      } else {
        setError(data.message || `Failed to ${editingUser ? 'update' : 'create'} user`);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.name}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/user/${user.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'delete'
        })
      });

      if (response.ok) {
        setSuccess('User deleted successfully!');
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to delete user');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  // Function to toggle panchayat expansion
  const togglePanchayatExpansion = (userIndex) => {
    setExpandedPanchayats(prev => ({
      ...prev,
      [userIndex]: !prev[userIndex]
    }));
  };

  // Function to render panchayat display with proper formatting
  const renderPanchayatDisplay = (panchayat, userIndex) => {
    if (!panchayat) return '-';
    
    let panchayatArray = [];
    if (Array.isArray(panchayat)) {
      panchayatArray = panchayat;
    } else if (typeof panchayat === 'string') {
      panchayatArray = [panchayat];
    }
    
    if (panchayatArray.length === 0) return '-';
    
    // If only one panchayat, show it directly
    if (panchayatArray.length === 1) {
      return (
        <div className="text-sm text-gray-900">
          {panchayatArray[0]}
        </div>
      );
    }
    
    // If multiple panchayats, show count initially and expand on click
    const isExpanded = expandedPanchayats[userIndex];
    
    return (
      <div className="max-w-xs">
        {!isExpanded ? (
          <button
            onClick={() => togglePanchayatExpansion(userIndex)}
            className="text-sm text-blue-600 hover:text-blue-800 cursor-pointer underline"
            title="Click to see all panchayats"
          >
            {panchayatArray.length} panchayats
          </button>
        ) : (
          <div>
            <button
              onClick={() => togglePanchayatExpansion(userIndex)}
              className="text-xs text-blue-600 hover:text-blue-800 cursor-pointer underline mb-1"
            >
              Hide ({panchayatArray.length})
            </button>
            <div className="text-sm text-gray-900">
              {panchayatArray.map((p, index) => (
                <div key={index} className="truncate">
                  {p}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const shouldShowPanchayat = ['dist-bdo', 'pncyt-pre', 'pncyt-bdo', 'pncyt-ae', 'pncyt-je', 'overseer', 'motivator', 'zonal'].includes(formData.role);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <p className="mt-1 text-gray-600">Create and manage system users</p>
            </div>
            <button
              onClick={() => {
                setShowForm(!showForm);
                if (!showForm) resetForm();
              }}
              className="mt-4 sm:mt-0 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition duration-200 font-medium"
            >
              {showForm ? 'Cancel' : 'Create New User'}
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg mb-4">
            {success}
          </div>
        )}

        {/* Create/Edit User Form */}
        {showForm && (
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              {editingUser ? 'Edit User' : 'Create New User'}
            </h2>
            
            <div className="space-y-4">
              {/* Compact Form Layout */}
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {/* Role Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Designation *</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Designation</option>
                    {roleOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter full name"
                  />
                </div>

                {/* Contact Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                  <input
                    type="tel"
                    name="contact"
                    value={formData.contact}
                    onChange={handleInputChange}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter contact number"
                  />
                </div>

                {/* Block Selection with Checkboxes */}
                {shouldShowPanchayat && (
                  <div ref={blockDropdownRef}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Block *</label>
                    
                    {/* Selected Blocks as Tags */}
                    {Array.isArray(formData.blocks) && formData.blocks.length > 0 && (
                      <div className="mb-2 flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                        {formData.blocks.map((block, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-100 text-green-800"
                          >
                            {block}
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  blocks: prev.blocks.filter(b => b !== block),
                                  panchayat: [] // Clear panchayats when blocks change
                                }));
                                const remainingBlocks = formData.blocks.filter(b => b !== block);
                                if (remainingBlocks.length > 0) {
                                  fetchPanchayats(remainingBlocks[0]);
                                } else {
                                  setPanchayatOptions([]);
                                }
                              }}
                              className="ml-1 text-green-600 hover:text-green-800"
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <div className="relative">
                      <input
                        type="text"
                        value={blockSearch}
                        onChange={(e) => setBlockSearch(e.target.value)}
                        onFocus={() => setShowBlockDropdown(true)}
                        placeholder="Search blocks..."
                        className="w-full px-2 py-1.5 pr-6 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <ChevronDown className="absolute right-2 top-2 h-3 w-3 text-gray-400 pointer-events-none" />
                      
                      {showBlockDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-auto">
                          {/* Select All Option */}
                          <div className="px-2 py-1.5 border-b border-gray-100">
                            <label className="flex items-center text-xs font-medium text-blue-600 cursor-pointer hover:bg-blue-50">
                              <input
                                type="checkbox"
                                checked={blockOptions.length > 0 && blockOptions.every(block => formData.blocks.includes(block.name || block))}
                                onChange={handleSelectAllBlocks}
                                className="mr-2 text-blue-600 focus:ring-blue-500"
                              />
                              Select All Blocks
                            </label>
                          </div>
                          
                          {filteredBlocks.length > 0 ? (
                            filteredBlocks.map((block, index) => {
                              const blockName = block.name || block;
                              const isSelected = formData.blocks.includes(blockName);
                              
                              return (
                                <div key={block.id || index} className="px-2 py-1">
                                  <label className="flex items-center text-xs cursor-pointer hover:bg-gray-50">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleBlockToggle(block)}
                                      className="mr-2 text-blue-600 focus:ring-blue-500"
                                    />
                                    {blockName}
                                  </label>
                                </div>
                              );
                            })
                          ) : (
                            <div className="px-2 py-1 text-xs text-gray-500">No blocks found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Panchayat Selection with Checkboxes */}
              {shouldShowPanchayat && (
                <div className="max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Panchayat *</label>
                  
                  {/* Selected Panchayats Count */}
                  {Array.isArray(formData.panchayat) && formData.panchayat.length > 0 && (
                    <div className="mb-2 text-xs text-gray-600">
                      {formData.panchayat.length} panchayat{formData.panchayat.length > 1 ? 's' : ''} selected
                    </div>
                  )}

                  {/* Panchayat Dropdown */}
                  <div className="relative w-64" ref={panchayatDropdownRef}>
                    <input
                      type="text"
                      value={panchayatSearch}
                      onChange={(e) => setPanchayatSearch(e.target.value)}
                      onFocus={() => {
                        setShowPanchayatDropdown(true);
                        // Re-fetch panchayats when focusing if we have blocks selected
                        if (formData.blocks.length > 0 && panchayatOptions.length === 0) {
                          fetchPanchayats(formData.blocks[0]);
                        }
                      }}
                      placeholder={formData.blocks.length > 0 ? "Search panchayats..." : "Select Block first"}
                      disabled={formData.blocks.length === 0}
                      className="w-full px-2 py-1.5 pr-6 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                    <ChevronDown className="absolute right-2 top-2 h-3 w-3 text-gray-400 pointer-events-none" />
                    
                    {showPanchayatDropdown && formData.blocks.length > 0 && (
                      <div className="absolute z-20 w-64 mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-auto">
                        {/* Select All Option */}
                        {panchayatOptions.length > 0 && (
                          <div className="px-2 py-1.5 border-b border-gray-100">
                            <label className="flex items-center text-xs font-medium text-blue-600 cursor-pointer hover:bg-blue-50">
                              <input
                                type="checkbox"
                                checked={panchayatOptions.length > 0 && panchayatOptions.every(panchayat => formData.panchayat.includes(panchayat.name || panchayat))}
                                onChange={handleSelectAllPanchayats}
                                className="mr-2 text-blue-600 focus:ring-blue-500"
                              />
                              Select All Panchayats
                            </label>
                          </div>
                        )}
                        
                        {filteredPanchayats.length > 0 ? (
                          filteredPanchayats.map((panchayat, index) => {
                            const panchayatName = panchayat.name || panchayat;
                            const isSelected = formData.panchayat.includes(panchayatName);
                            
                            return (
                              <div key={panchayat.id || index} className="px-2 py-1">
                                <label className="flex items-center text-xs cursor-pointer hover:bg-gray-50">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handlePanchayatToggle(panchayat)}
                                    className="mr-2 text-blue-600 focus:ring-blue-500"
                                  />
                                  {panchayatName}
                                </label>
                              </div>
                            );
                          })
                        ) : (
                          <div className="px-2 py-1 text-xs text-gray-500">No panchayats found</div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Show selected panchayats as chips below */}
                  {Array.isArray(formData.panchayat) && formData.panchayat.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                      {formData.panchayat.map((panchayat, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                        >
                          {panchayat}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                panchayat: prev.panchayat.filter(p => p !== panchayat)
                              }));
                            }}
                            className="ml-1 text-blue-600 hover:text-blue-800"
                          >
                            <X className="h-2.5 w-2.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-1">Select multiple panchayats</p>
                </div>
              )}

              {/* Form Actions */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creating ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users List */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Existing Users ({users.length})</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Number</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Block</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Panchayat</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user, index) => {
                  const roleLabel = roleOptions.find(role => role.value === user.role)?.label || user.role;
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {roleLabel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {user.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {user.username || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {user.blocks && user.blocks.length > 0 ? (
                          <div className="max-w-xs truncate">
                            {user.blocks.join(', ')}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {renderPanchayatDisplay(user.panchayat, index)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(user)}
                            className="text-blue-600 hover:text-blue-800 transition duration-200"
                            title="Edit User"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(user)}
                            className="text-red-600 hover:text-red-800 transition duration-200"
                            title="Delete User"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {users.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No users found. Create your first user to get started.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;